import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { faCircle } from '@fortawesome/free-regular-svg-icons/faCircle';
import { faCircleDot } from '@fortawesome/free-solid-svg-icons/faCircleDot';

import FaIcon from 'ravenjs/lib/FaIcon';
import SwitchBase from 'ravenjs/lib/SwitchBase';
import { themeGet } from 'ravenjs/utils/theme';

/**
 * Generate the radio icon based on the given type.
 *
 * @method getRadioIcon
 * @param  {string}       type One of `checked` or null
 * @return {Function}          A React functional component
 */
function getRadioIcon(type) {
    const icon = type === 'checked' ? faCircleDot : faCircle;
    // Return a React functional component.
    return function RadioIcon(config) {
        const { checked, activeIconColor, inactiveIconColor, isDisabled, ...rest } = config;
        // Determine the color of the Icon based on the `checked` value.
        const color = checked ? activeIconColor : inactiveIconColor;
        // Render the Icon.
        return <FaIcon icon={icon} color={color} disabled={isDisabled} {...rest} />;
    };
}

/**
 * Generate a table-style radio icon (ring with inner filled dot).
 * Used when type="innerFilledRadio" is passed to Radio component.
 *
 * @method getTableRadioIcon
 * @param  {string}       type One of `checked` or null
 * @return {Function}          A React functional component
 */
function getTableRadioIcon(type) {
    function TableRadioIcon({ checked, activeIconColor, inactiveIconColor, isDisabled, ...rest }) {
        const borderColor = inactiveIconColor || '#282828';
        const fillColor = activeIconColor || '#808285';

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    verticalAlign: 'middle',
                    lineHeight: 0,
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: `1px solid ${borderColor}`,
                    backgroundColor: 'transparent',
                    boxSizing: 'border-box',
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
                {...rest}
            >
                <span
                    style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        backgroundColor: fillColor,
                        opacity: checked ? 1 : 0,
                        transition: 'opacity 0.1s ease-out',
                    }}
                />
            </span>
        );
    }

    TableRadioIcon.propTypes = {
        checked: PropTypes.bool.isRequired,
        activeIconColor: PropTypes.string.isRequired,
        inactiveIconColor: PropTypes.string.isRequired,
        isDisabled: PropTypes.bool.isRequired,
    };

    return TableRadioIcon;
}

const RadioStyled = styled(SwitchBase)`
    cursor: pointer;
    position: relative;
    /**
     * Add all of the remaining styles from theme
     */
    ${themeGet('Radio.styled')};
`;

function Radio(props) {
    const {
        checkedIcon: checkedIconProp,
        disabled,
        icon: iconProp,
        color,
        inactiveColor,
        type,
        ...other
    } = props;
    let resolvedIcon = iconProp;
    let resolvedCheckedIcon = checkedIconProp;

    if (!resolvedIcon || !resolvedCheckedIcon) {
        if (type === 'innerFilledRadio') {
            resolvedIcon = resolvedIcon || getTableRadioIcon();
            resolvedCheckedIcon = resolvedCheckedIcon || getTableRadioIcon('checked');
        } else {
            resolvedIcon = resolvedIcon || getRadioIcon();
            resolvedCheckedIcon = resolvedCheckedIcon || getRadioIcon('checked');
        }
    }
    return (
        <RadioStyled
            checkedIcon={resolvedCheckedIcon}
            isDisabled={disabled}
            activeIconColor={color}
            inactiveIconColor={inactiveColor}
            icon={resolvedIcon}
            height="16px"
            width="16px"
            type="radio"
            {...other}
        />
    );
}

Radio.propTypes = {
    /**
     * If `true`, the component is marked as checked.
     */
    checked: PropTypes.bool,
    /**
     * The icon to display when the component is checked.
     */
    checkedIcon: PropTypes.func,
    /**
     * Apply themed styling to Radio in `active` state.
     *
     * Colors can be defined in `theme.palette`.
     */
    color: PropTypes.string,
    /**
     * If `true`, the Button will be disabled.
     */
    disabled: PropTypes.bool,
    /**
     * The Icon to display when the component is unchecked.
     */
    icon: PropTypes.func,
    /**
     * The id for the `input` element.
     */
    id: PropTypes.string,
    /**
     * Apply themed styling to Radio in `inactive` state.
     *
     * Colors can be defined in `theme.palette`.
     */
    inactiveColor: PropTypes.string,
    /**
     * The attributes for the `input` element.
     */
    inputProps: PropTypes.object,
    /**
     * Attaches a React ref to the native input component.
     * Can use a `node => this.ref = node` function, or a `React.createRef()` object.
     */
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    /**
     * Callback fired when the internal state is changed.
     */
    onChange: PropTypes.func,
    /**
     * The `type` of internal input.
     */
    type: PropTypes.string,
    /**
     * The value of the internal input component.
     */
    value: PropTypes.any,
};

Radio.defaultProps = {
    checked: false,
    checkedIcon: undefined,
    color: null,
    disabled: false,
    icon: undefined,
    id: null,
    inactiveColor: null,
    inputProps: null,
    inputRef: null,
    onChange: null,
    type: 'radio',
    value: null,
};

export default Radio;
